import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";
import BlogAPI from "../api/blog";
import SEO from "../components/SEO";
import { useSchemaMicrodata } from '../hooks/useSchemaMicrodata';
import BlogCard from "../components/blog/BlogCard";
import FeaturedPost from "../components/blog/FeaturedPost";
import CategoryFilter from "../components/blog/CategoryFilter";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useSchemaMicrodata('blog', {
    name: 'Pharmacy Wiki Blog',
    description: 'Expert insights on medications, pharmaceutical industry news, drug safety, and healthcare information',
    url: window.location.href
  });

  useEffect(() => {
    fetchData();
  }, [page, selectedCategory]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch posts
      const postsData = await BlogAPI.getPosts({
        page,
        limit: 12,
        category: selectedCategory !== 'all' ? selectedCategory : ''
      });
      
      setPosts(postsData.results || []);
      setTotalPages(postsData.totalPages || 1);

      // Fetch featured posts only on first page
      if (page === 1 && selectedCategory === 'all') {
        const featured = await BlogAPI.getFeaturedPosts(3);
        setFeaturedPosts(featured || []);
      }

      // Fetch categories
      if (categories.length === 0) {
        const categoriesData = await BlogAPI.getCategories();
        setCategories(categoriesData || []);
      }
    } catch (error) {
      console.error('Error fetching blog data:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Pharmacy Blog - Expert Healthcare & Medication Insights"
        description="Stay informed with expert articles on medications, drug safety, pharmaceutical news, and healthcare tips from Pharmacy Wiki"
        keywords="pharmacy blog, medication guides, drug safety, pharmaceutical news, healthcare tips"
      />

      <div className="container mx-auto px-6 mt-[90px] max-md:mt-[40px]">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-medium text-48 text-secondary max-md:text-36">
            Pharmacy <span className="text-primary">Blog</span>
          </h1>
          <p className="mt-4 text-18 text-light-gray/80 max-md:text-14">
            Expert insights on medications, drug safety, and healthcare
          </p>
        </div>

        {/* Featured Posts */}
        {!isLoading && featuredPosts.length > 0 && page === 1 && selectedCategory === 'all' && (
          <div className="mb-16">
            <h2 className="text-26 font-medium text-secondary mb-6 max-md:text-20">Featured Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.map((post) => (
                <FeaturedPost key={post._id} post={post} />
              ))}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {/* Blog Posts Grid */}
        <div className="mt-8">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg overflow-hidden border border-stroke">
                  <Skeleton height={200} />
                  <div className="p-6">
                    <Skeleton count={1} height={24} className="mb-2" />
                    <Skeleton count={3} />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <BlogCard key={post._id} post={post} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-12">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg bg-white border border-stroke text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-secondary">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg bg-white border border-stroke text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <p className="text-light-gray text-18">No blog posts found</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
